import React from 'react';
import { motion } from 'framer-motion';
import {
  Home,
  MapPin,
  Leaf,
  BarChart3,
  Calculator,
  BookOpen,
} from 'lucide-react';
import './TabNavigation.css';

export type TabType = 'overview' | 'gallery' | 'species' | 'environmental' | 'carbon' | 'references';

interface TabNavigationProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  showReferences: boolean;
}

interface TabItem {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  description: string;
  hidden?: boolean;
}

const tabs: TabItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: <Home size={20} />,
    description: '',
  },
  {
    id: 'gallery',
    label: 'Virtual Garden',
    icon: <MapPin size={20} />,
    description: '',
  },
  {
    id: 'species',
    label: 'Species',
    icon: <Leaf size={20} />,
    description: '',
  },
  {
    id: 'environmental',
    label: 'Environmental Impact',
    icon: <BarChart3 size={20} />,
    description: '',
  },
  {
    id: 'carbon',
    label: 'Carbon Calculator',
    icon: <Calculator size={20} />,
    description: '',
  },
  {
    id: 'references',
    label: 'References',
    icon: <BookOpen size={20} />,
    description: '',
    hidden: true,
  },
];

export const TabNavigation: React.FC<TabNavigationProps> = ({
  activeTab,
  onTabChange,
  showReferences,
}) => {
  const visibleTabs = tabs.filter(tab => !tab.hidden || (tab.hidden && showReferences));

  return (
    <div className="tab-navigation">
      <div className="tab-navigation__container">
        <div className="tab-navigation__tabs" role="tablist">
          {visibleTabs.map((tab, index) => {
            const isActive = activeTab === tab.id;

            return (
              <motion.button
                key={tab.id}
                className={`tab-navigation__tab ${isActive ? 'tab-navigation__tab--active' : ''}`}
                onClick={() => onTabChange(tab.id)}
                role="tab"
                aria-selected={isActive}
                aria-controls={`${tab.id}-panel`}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="tab-navigation__icon">{tab.icon}</span>
                <span className="tab-navigation__label">{tab.label}</span>
                <span className="tab-navigation__description">{tab.description}</span>

                {isActive && (
                  <motion.div
                    className="tab-navigation__indicator"
                    layoutId="activeTab"
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TabNavigation;
